import { fetchMyLegalConsentStatus } from '../api/profileApi';
import { waitForSupabaseSession } from './authSession';
import { getSupabase } from './supabase';
import { LEGAL_CONSENT_VERSION } from './legalConsentVersion';
import {
  isSetupCompleteStorage,
  isSignupFlowActive,
  loadRegions,
  setLoggedInTrue,
  setSetupComplete,
} from './onboardingStorage';

/**
 * terms·privacy 타임스탬프 누락, 또는 consent_version이 현재 약관 버전과 다르면 재동의 필요.
 * @param {{
 *   terms_agreed_at?: string | null,
 *   privacy_agreed_at?: string | null,
 *   consent_version?: string | null,
 * } | null | undefined} record
 */
export function needsLegalConsent(record) {
  if (!record) return true;
  if (!record.terms_agreed_at || !record.privacy_agreed_at) return true;
  const ver = String(record.consent_version || '').trim();
  return ver !== LEGAL_CONSENT_VERSION;
}

/** @param {string | null | undefined} raw */
export function safePostAuthPath(raw) {
  const s = typeof raw === 'string' ? raw.trim() : '';
  if (!s.startsWith('/') || s.startsWith('//')) return '/home';
  const pathOnly = s.split('?')[0];
  if (pathOnly === '/edit-profile' || pathOnly === '/settings/account' || pathOnly === '/profile') return '/home';
  if (pathOnly === '/onboarding/oauth-consent') return '/home';
  return s;
}

/**
 * 세션 복원 후 consent 조회 (동의 게이트 SSOT).
 * @param {import('@supabase/supabase-js').User | null | undefined} [preloadedUser]
 * @param {{ maxMs?: number }} [opts]
 */
export async function fetchLegalConsentAfterSession(preloadedUser, opts = {}) {
  if (preloadedUser) {
    return fetchMyLegalConsentStatus(preloadedUser);
  }
  const supabase = getSupabase();
  if (!supabase) return null;
  const session = await waitForSupabaseSession(supabase, { maxMs: opts.maxMs ?? 4500 });
  if (!session?.user) return null;
  return fetchMyLegalConsentStatus(session.user);
}

/**
 * @param {{ checkSignupPrefs?: boolean, nextPath?: string, preloadedUser?: import('@supabase/supabase-js').User | null }} [options]
 * @returns {Promise<{ path: string, needsPrefsState: boolean }>}
 */
export async function resolvePostAuthPath(options = {}) {
  const { checkSignupPrefs = true, nextPath, preloadedUser } = options;

  if (checkSignupPrefs && isSignupFlowActive() && !isSetupCompleteStorage()) {
    return { path: '/onboarding/preferences', needsPrefsState: true };
  }

  const status = await fetchLegalConsentAfterSession(preloadedUser);
  if (needsLegalConsent(status)) {
    return { path: '/onboarding/oauth-consent', needsPrefsState: false };
  }

  return { path: safePostAuthPath(nextPath), needsPrefsState: false };
}

/**
 * 로그인·OAuth 콜백 후 공통 리다이렉트 (콜백 본문 로직은 유지, navigate만 분기).
 * @param {import('react-router-dom').NavigateFunction} navigate
 * @param {{ refreshAuth?: () => void, nextPath?: string, checkSignupPrefs?: boolean, replace?: boolean, preloadedUser?: import('@supabase/supabase-js').User | null }} [options]
 */
export async function finishAuthenticatedSession(navigate, options = {}) {
  const { refreshAuth, nextPath, checkSignupPrefs = true, replace = true, preloadedUser } = options;

  setLoggedInTrue();

  const { path, needsPrefsState } = await resolvePostAuthPath({
    checkSignupPrefs,
    nextPath,
    preloadedUser,
  });

  if (path !== '/onboarding/oauth-consent') {
    setSetupComplete();
  }

  refreshAuth?.();

  if (path === '/onboarding/preferences' && needsPrefsState) {
    navigate('/onboarding/preferences', {
      replace,
      state: { city: loadRegions()?.[0] || 'Da Nang' },
    });
    return;
  }

  navigate(path, { replace });
}
